/**
 * iter150AZ.78 — Pure helpers for the sequencing Weekly Stop Schedule
 * editor. Extracted so unit tests can target them without dragging in
 * the rest of the React component.
 *
 * Windows use the form `{ start_time: "HH:MM", end_time: "HH:MM" }`
 * where both times are 24h within a single day (cross-midnight windows
 * are not supported; the user splits them across two weekdays).
 * `end_time` is exclusive — i.e. 12:00-13:00 and 13:00-14:00 are
 * back-to-back, NOT overlapping.
 */

/** Parse "HH:MM" to a number of minutes since midnight, or NaN. */
export function _toMinutes(s) {
  if (typeof s !== "string") return NaN;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return NaN;
  const h = Number(m[1]);
  const mn = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mn)) return NaN;
  if (h < 0 || h > 23 || mn < 0 || mn > 59) return NaN;
  return h * 60 + mn;
}

/** Format minutes-since-midnight back to "HH:MM" (24h). */
export function _toHHMM(mins) {
  if (!Number.isFinite(mins)) return "";
  // Clamp to [0, 1439]; a same-day window cannot start at 24:00.
  const m = Math.max(0, Math.min(1439, Math.round(mins)));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${String(h).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

/** Returns true iff windows A and B overlap on the same day.
 *  End-time is exclusive, so [12:00,13:00) and [13:00,14:00) do NOT
 *  overlap. A degenerate window (start>=end, or missing fields)
 *  never overlaps anything — those rows are caught by the
 *  "start must be earlier than end" check on save. */
export function windowsOverlap(a, b) {
  const aS = _toMinutes(a?.start_time);
  const aE = _toMinutes(a?.end_time);
  const bS = _toMinutes(b?.start_time);
  const bE = _toMinutes(b?.end_time);
  if (!Number.isFinite(aS) || !Number.isFinite(aE)) return false;
  if (!Number.isFinite(bS) || !Number.isFinite(bE)) return false;
  if (aE <= aS || bE <= bS) return false;
  return aS < bE && bS < aE;
}

/** Returns an array of the same length as `windows` whose values are
 *  true at index i iff window[i] overlaps any other window in the
 *  same day. Used for inline per-row "Overlaps another window" UI. */
export function overlapMask(windows) {
  const n = Array.isArray(windows) ? windows.length : 0;
  const mask = new Array(n).fill(false);
  if (n < 2) return mask;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (windowsOverlap(windows[i], windows[j])) {
        mask[i] = true;
        mask[j] = true;
      }
    }
  }
  return mask;
}

/** Returns true if ANY day in the byDay map has overlapping windows. */
export function hasAnyOverlap(byDay) {
  if (!byDay || typeof byDay !== "object") return false;
  for (const list of Object.values(byDay)) {
    const m = overlapMask(list);
    if (m.some(Boolean)) return true;
  }
  return false;
}


/** iter150AZ.82 — permissive disabled-predicate for the START
 *  picker. A start time T is impossible to use iff T falls INSIDE
 *  another window's [s, e) range — because then ANY end > T would
 *  overlap. Times OUTSIDE every other window are usable (the user
 *  just has to pick a compatible end too; that's the same-row
 *  constraint handled by patchWindow's auto-snap).
 *
 *  Note: ``end-time exclusive`` means T == s OR T == e are NOT
 *  blocked (back-to-back windows are fine). */
export function startIsBlocked(t, others) {
  if (typeof t !== "string" || !Array.isArray(others)) return false;
  for (const o of others) {
    if (!o?.start_time || !o?.end_time) continue;
    if (t >= o.start_time && t < o.end_time) return true;
  }
  return false;
}

/** Symmetric predicate for the END picker — T is impossible iff
 *  T falls inside another window's (s, e] range. */
export function endIsBlocked(t, others) {
  if (typeof t !== "string" || !Array.isArray(others)) return false;
  for (const o of others) {
    if (!o?.start_time || !o?.end_time) continue;
    if (t > o.start_time && t <= o.end_time) return true;
  }
  return false;
}

/** Pick the first non-overlapping `defaultMinutes`-long slot starting
 *  at or after `preferredStart` (minutes since midnight). Returns
 *  `{ start_time, end_time }` HH:MM strings.
 *
 *  Strategy: try the preferred start first; if it collides, walk in
 *  15-minute increments until we find a free slot. Falls back to the
 *  last possible 1-hour slot of the day (23:00-23:59) if nothing fits.
 */
export function suggestNextWindow(existing, {
  preferredStart = 12 * 60,             // default to 12:00
  defaultMinutes = 60,                  // default 1h length
  stepMinutes    = 15,                  // alignment grid
} = {}) {
  const list = Array.isArray(existing) ? existing : [];
  // Snap to the alignment grid so the suggested window is tidy.
  const snap = (m) => Math.round(m / stepMinutes) * stepMinutes;
  let s = snap(preferredStart);
  const lastValidStart = 23 * 60 + 45;  // ≥ 15-min slot before midnight
  // Walk forward in step-sized hops until we find a non-colliding slot.
  while (s <= lastValidStart) {
    const e = Math.min(s + defaultMinutes, 23 * 60 + 59);
    if (e <= s) break;
    const candidate = { start_time: _toHHMM(s), end_time: _toHHMM(e) };
    const collides = list.some(w => windowsOverlap(candidate, w));
    if (!collides) return candidate;
    s += stepMinutes;
  }
  // Wrap to the beginning of the day if nothing fits after the
  // preferred start (e.g. preferred was 12:00 but lunch is taken;
  // try 00:00 onward).
  s = 0;
  while (s < snap(preferredStart)) {
    const e = Math.min(s + defaultMinutes, 23 * 60 + 59);
    const candidate = { start_time: _toHHMM(s), end_time: _toHHMM(e) };
    if (!list.some(w => windowsOverlap(candidate, w))) return candidate;
    s += stepMinutes;
  }
  // Last resort — the day is fully covered. Return a minimal slot
  // that will collide but still gives the user something to edit.
  return { start_time: "23:00", end_time: "23:59" };
}
